import {
  MeetOpsQueryResult,
  QueryResult,
} from "@app/analytics/hooks/metrics/useMetricsQuery";
import {
  QueryNumberDimension,
  QueryStringDimension,
} from "@app/analytics/meetops/dimensions";
import { ParsedMetric } from "@app/analytics/meetops/metrics";
import { QueryMetric } from "@app/analytics/meetops/types";
import { RequiredBy } from "@app/common/types";

export type PartialParsedMetric = Pick<
  ParsedMetric,
  "value" | "formattedValue"
>;

type ParsedQueryMetricsResult = {
  // don't use the full ParsedMetric type here so this type can also be used
  // for manually-constructed results (e.g., hard-coded Read Averages)
  [key in QueryMetric]?: PartialParsedMetric;
};

type ParsedQueryStringDimensionsResult = {
  [key in QueryStringDimension]?: string | null;
};

type ParsedQueryNumberDimensionsResult = {
  [key in QueryNumberDimension]?: number | null;
};

export type ParsedQueryResult = ParsedQueryMetricsResult &
  ParsedQueryStringDimensionsResult &
  ParsedQueryNumberDimensionsResult;

export type ParsedQueryResultRequiredBy<K extends keyof ParsedQueryResult> =
  RequiredBy<ParsedQueryResult, K>;

export const getMetricValue = (
  result: ParsedQueryMetricsResult | undefined,
  metric: QueryMetric
): ParsedMetric["value"] => (result ? result[metric]?.value ?? null : null);

export const getMetricFormattedValue = (
  result: ParsedQueryMetricsResult | undefined,
  metric: QueryMetric
): ParsedMetric["formattedValue"] =>
  (result ? result[metric]?.formattedValue : "-") || "-";

export const parseMetricsDataListFromQueryResult = <T>(
  queryResult: MeetOpsQueryResult | undefined
): T[][] => {
  return (queryResult?.results ?? []).map(
    (result: QueryResult) => result?.data
  );
};

export const parseMetricsDataFromQueryResult = <T>(
  queryResult: MeetOpsQueryResult | undefined,
  defaultValue: T
): T[] => {
  return (queryResult?.results ?? []).map(
    (result: QueryResult) => result?.data[0] ?? defaultValue
  );
};
